import { useCallback, useEffect, useRef } from 'react';
import { Box, CircularProgress, Container, Typography } from '@mui/material';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { LOAN_PATH } from '@/config/loanNavPaths';
import { LoanFlowChrome } from '@/components/Layout/Loan/LoanFlowChrome';
import { LoanFlowPageFrame } from '@/components/Layout/Loan/LoanFlowStatusBar';
import { PersonalFinancialStatementForm } from '@/components/PersonalFinancialStatement/PersonalFinancialStatementForm';
import { InvestorProgramsPanel } from '@/components/Profile/InvestorProgramsPanel';
import { ProfileAdvancedSettings } from '@/components/Profile/ProfileAdvancedSettings';
import { ProfileLoanType } from '@/components/Profile/ProfileLoanType';
import { useProperties } from '@/hooks/useProperties';
import { useStartLoanApplication } from '@/hooks/useStartLoanApplication';
import { CommercialLoanApplicationPage } from '@/pages/App/CommercialLoanApplicationPage';
import { CommercialLoanHubPage } from '@/pages/App/CommercialLoanHubPage';

export function LoanHubRoute() {
  return (
    <LoanFlowChrome>
      <CommercialLoanHubPage />
    </LoanFlowChrome>
  );
}

/**
 * Loan type: workflow (purchase / refinance / investor), program and
 * occupancy, then qualifications (income, credit, debt and buying power) on
 * the same page.
 */
export function LoanLoanTypeRoute() {
  const location = useLocation();
  const qualificationsRef = useRef<HTMLDivElement>(null);
  const scrollToQualifications = useCallback(() => {
    qualificationsRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  // Links to /loan/qualifications land here with #qualifications.
  useEffect(() => {
    if (location.hash === `#${LOAN_PATH.qualificationsSection}`) {
      const id = window.setTimeout(scrollToQualifications, 50);
      return () => window.clearTimeout(id);
    }
  }, [location.hash, scrollToQualifications]);

  return (
    <LoanFlowChrome>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <LoanFlowPageFrame>
          <ProfileLoanType
            onContinueToQualifications={scrollToQualifications}
          />
          <Box
            id={LOAN_PATH.qualificationsSection}
            ref={qualificationsRef}
            sx={{ mt: 4, scrollMarginTop: 96 }}
          >
            <ProfileAdvancedSettings />
          </Box>
          <Box sx={{ mt: 3 }}>
            <InvestorProgramsPanel />
          </Box>
        </LoanFlowPageFrame>
      </Container>
    </LoanFlowChrome>
  );
}

/**
 * Loan qualifications now live on the Loan type page. Keep the query string
 * (e.g. returnTo, so "Save and continue" still goes back where it came from).
 */
export function LoanQualificationsRoute() {
  const location = useLocation();
  return (
    <Navigate
      to={`${LOAN_PATH.loanType}${location.search}#${LOAN_PATH.qualificationsSection}`}
      replace
    />
  );
}

/** Commercial/investor qualification: net worth, assets/liabilities, and schedule of real estate owned. */
export function LoanPersonalFinancialStatementRoute() {
  return (
    <LoanFlowChrome>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <LoanFlowPageFrame>
          <PersonalFinancialStatementForm />
        </LoanFlowPageFrame>
      </Container>
    </LoanFlowChrome>
  );
}

/** Loan status now lives on My applications; keep any query string. */
export function LoanStatusRoute() {
  const location = useLocation();
  return (
    <Navigate
      to={`${LOAN_PATH.hub}${location.search}#${LOAN_PATH.loanStatusSection}`}
      replace
    />
  );
}

/** Mortgage documents live on the Documents page, which has the same checklist. */
export function LoanDocumentsRoute() {
  const location = useLocation();
  return (
    <Navigate
      to={`${LOAN_PATH.documents}${location.search}${location.hash}`}
      replace
    />
  );
}

export function LoanApplicationRoute() {
  return (
    <LoanFlowChrome>
      <CommercialLoanApplicationPage />
    </LoanFlowChrome>
  );
}

/**
 * `/loan/start?property=<productId>` — opens (or creates) the application for
 * that home and lands on the form. Pre-qualification returns here so a new
 * borrower goes property → pre-qual → application with no hub in between.
 */
export function LoanStartRoute() {
  const [params] = useSearchParams();
  const productId = params.get('property');
  const { allProperties, isLoading } = useProperties();
  const { start } = useStartLoanApplication();
  const started = useRef(false);

  useEffect(() => {
    if (started.current || isLoading) return;
    started.current = true;
    const property = productId
      ? allProperties.find((p) => p.productId === productId)
      : undefined;
    void start(property);
  }, [allProperties, isLoading, productId, start]);

  return (
    <Box sx={{ py: 10, textAlign: 'center' }}>
      <CircularProgress size={32} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Opening your application…
      </Typography>
    </Box>
  );
}
